import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, TrendingUp } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import { fetchStylists } from '@/lib/api/stylists'
import {
  exportStylistPerformanceReport,
  fetchStylistPerformanceReport,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function StylistPerformancePanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [stylistId, setStylistId] = useState('')
  const [stylists, setStylists] = useState([])
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
      stylistId: stylistId === '' ? null : Number(stylistId),
    }),
    [period, customFrom, customTo, bounds, compare, stylistId],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchStylistPerformanceReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load stylist performance report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    let active = true

    fetchStylists()
      .then((items) => {
        if (active) setStylists(items.filter((item) => item.is_active))
      })
      .catch(() => {
        if (active) setStylists([])
      })

    return () => {
      active = false
    }
  }, [activeBranchId])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportStylistPerformanceReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <TrendingUp className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Stylist performance</h2>
            <p className="text-sm text-muted-foreground">
              Service revenue, commission, and direct tips per stylist for the selected period.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        >
          <select
            value={stylistId}
            onChange={(e) => setStylistId(e.target.value)}
            className="h-9 rounded-xl border border-border/80 bg-card px-3 text-sm"
            aria-label="Filter by stylist"
          >
            <option value="">All stylists</option>
            {stylists.map((stylist) => (
              <option key={stylist.id} value={stylist.id}>
                {stylist.name}
              </option>
            ))}
          </select>
        </ReportFilterBar>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Stylists" value={String(report.summary.stylist_count)} />
            <SummaryCard
              label="Service revenue"
              value={`AED ${formatReportMoney(report.summary.service_revenue)}`}
            />
            <SummaryCard label="Services sold" value={String(report.summary.service_quantity)} />
            <SummaryCard
              label="Commission"
              value={`AED ${formatReportMoney(report.summary.commission_total)}`}
            />
            <SummaryCard
              label="Direct tips"
              value={`AED ${formatReportMoney(report.summary.direct_tips)}`}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period revenue: AED {formatReportMoney(report.compare_summary.service_revenue)}
              {' · '}
              commission AED {formatReportMoney(report.compare_summary.commission_total)}
            </p>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Stylist</th>
                  <th className="px-4 py-3 font-medium">Services</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">Revenue</th>
                  <th className="px-4 py-3 font-medium">Commission</th>
                  <th className="px-4 py-3 font-medium">Direct tips</th>
                </tr>
              </thead>
              <tbody>
                {report.by_stylist.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No stylist activity in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_stylist.map((row) => (
                    <tr key={row.stylist_id ?? row.stylist_name} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{row.stylist_name ?? '—'}</td>
                      <td className="px-4 py-3">{row.service_lines}</td>
                      <td className="px-4 py-3">{row.service_quantity}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.service_revenue)}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.commission_total)}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.direct_tips)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
